import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { OutstandingDisplay } from 'components/common';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useParties } from 'hooks/api/useCommercial';
import { partyService, taxMetaService, taxProfileService, referenceService, currencyService, customerGroupService } from 'api/services';
import TaxFieldsPanel, { validateTaxFields } from 'components/modules/tax/TaxFieldsPanel';
import type { PartyDto, PartyQueryParams, CreatePartyRequest, TaxFieldContextDto } from 'api/types';

const PARTY_TYPE_OPTIONS: SelectOption[] = [
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'SUPPLIER', label: 'Supplier' },
  { value: 'BOTH', label: 'Both' },
];
const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

interface FormData {
  partyCode: string; partyName: string; legalName: string; partyType: string;
  email: string; phone: string; currencyCode: string; creditDays: string; partyGroupId: string;
  address: string; city: string; postalCode: string; countryId: string; stateName: string; notes: string; statusCode: string;
}
const EMPTY: FormData = {
  partyCode: '', partyName: '', legalName: '', partyType: 'CUSTOMER', email: '', phone: '',
  currencyCode: '', creditDays: '', partyGroupId: '', address: '', city: '', postalCode: '', countryId: '', stateName: '', notes: '', statusCode: 'ACTIVE',
};

// ============================================================================
// FORM MODAL (hand-rolled — kept per-screen because tax-profile side-effect
// on save doesn't fit the EnterpriseFormModal wrapper's synchronous shape.
// Follows the same rules the wrapper enforces: fixed header per mode,
// no position:relative, slot-reuse via createInstanceId, record.code
// keying, minimizeRef for auto-minimize on new copy).
// ============================================================================

interface ClientFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: PartyDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const ClientFormModal: React.FC<ClientFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const snapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const [taxContext, setTaxContext] = useState<TaxFieldContextDto | null>(null);
  const [taxValues, setTaxValues] = useState<Record<string, any>>({});
  const taxRowVersionRef = useRef(0);
  const countryCache = useRef<Map<string, string>>(new Map());
  const currencyLabelCache = useRef<Map<string, string>>(new Map());

  const loadCurrencyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const list = await currencyService.getAll(input || undefined);
      list.forEach(c => currencyLabelCache.current.set(c.currencyCode, `${c.currencyCode} — ${c.currencyName}`));
      return list.map(c => ({ value: c.currencyCode, label: `${c.currencyCode} — ${c.currencyName}` }));
    } catch {
      return [];
    }
  }, []);

  // Refs for closure-safe minimize (see CLAUDE.md pattern-B five rules).
  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const formRef = useRef(form); formRef.current = form;
  const taxValuesRef = useRef(taxValues); taxValuesRef.current = taxValues;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    if (record?.countryId && record.countryName) countryCache.current.set(record.countryId, record.countryName);
    const src = (mode === 'edit' || mode === 'copy') ? record : null;
    const d: FormData = src ? {
      // Copy mode: blank code (user must supply a new one), preserve name+details as source
      partyCode: mode === 'copy' ? '' : src.partyCode,
      partyName: src.partyName, legalName: src.legalName || '', partyType: src.partyType,
      email: src.email || '', phone: src.phone || '',
      currencyCode: src.currencyCode || '', creditDays: src.creditDays != null ? String(src.creditDays) : '',
      partyGroupId: src.partyGroupId || '',
      address: src.addressLine1 || '', city: src.city || '', postalCode: src.postalCode || '',
      countryId: src.countryId || '', stateName: src.stateName || '', notes: src.notes || '',
      statusCode: src.statusCode ?? 'ACTIVE',
    } : { ...EMPTY };
    setForm(d); setTaxContext(null); setTaxValues({}); snapshotRef.current = '';
    (async () => {
      const ctx = await taxMetaService.getFieldContext('PARTY').catch(() => null);
      // The profile's attributes are the jurisdiction's PARTY fields, column-bound ones already
      // re-projected by the server, so the panel renders from one map (ADR-0047 §8).
      let tv: Record<string, any> = {};
      taxRowVersionRef.current = 0;
      if (ctx?.jurisdiction && src && mode === 'edit') {
        const prof = await taxProfileService.getPartyProfile(src.id).catch(() => null);
        if (prof) { tv = { ...(prof.attributes || {}) }; taxRowVersionRef.current = prof.rowVersion; }
      }
      setTaxContext(ctx); setTaxValues(tv);
      snapshotRef.current = JSON.stringify({ f: d, t: tv });
    })();
    setErrors({});
  }, [isOpen]);

  const isDirty = useMemo(
    () => snapshotRef.current ? JSON.stringify({ f: form, t: taxValues }) !== snapshotRef.current : false,
    [form, taxValues],
  );

  const updTax = (key: string, val: any) => setTaxValues(p => ({ ...p, [key]: val }));
  const upd = (patch: Partial<FormData>) => setForm(p => ({ ...p, ...patch }));
  const clearErr = (f: string) => setErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });
  const handleClose = () => { if (isDirty) setDiscardOpen(true); else onClose(); };

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const currentRecord = recordRef.current;
    const currentInstanceId = createInstanceIdRef.current;
    const savedForm = { ...formRef.current };
    const savedTax = { ...taxValuesRef.current };
    const savedSnapshot = snapshotRef.current;
    const dirty = savedSnapshot
      ? JSON.stringify({ f: savedForm, t: savedTax }) !== savedSnapshot
      : false;

    let modalId: string;
    let stripTitle: string;
    if (currentMode === 'create') {
      const idx = currentInstanceId || '1';
      modalId = `${tabId}-form-modal-create-${idx}`;
      const label = savedForm.partyCode.trim() || savedForm.partyName.trim();
      stripTitle = label ? `Add - ${label} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const key = currentRecord?.partyCode || currentInstanceId || '1';
      modalId = `${tabId}-form-modal-copy-${key}`;
      const label = savedForm.partyCode.trim() || currentRecord?.partyCode || '';
      stripTitle = label ? `Copy - ${label}` : 'Copy Client';
    } else {
      const key = currentRecord?.partyCode || currentRecord?.id || '';
      modalId = `${tabId}-form-modal-edit-${key}`;
      stripTitle = `Edit - ${currentRecord?.partyCode || currentRecord?.partyName || 'Client'}`;
    }

    minimize({
      id: modalId, title: stripTitle, moduleName, tabId, isDirty: dirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm); setTaxValues(savedTax); snapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentInstanceId : undefined,
          currentMode !== 'create' ? currentRecord?.partyCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  // Expose handleMinimize via ref for auto-minimize on new copy/add
  useEffect(() => {
    if (minimizeRef) minimizeRef.current = handleMinimize;
  }, [minimizeRef, handleMinimize]);

  const loadCountries = useCallback(async (input: string): Promise<SelectOption[]> => {
    // Swallow reference-API failures — react-select's internal async wrapping
    // otherwise surfaces the rejection to the window unhandled-rejection handler,
    // which shows a red toast for a background prefetch the user never asked for.
    try {
      const list = await referenceService.getCountries(input || undefined);
      list.forEach(c => countryCache.current.set(c.id, c.countryName));
      return list.map(c => ({ value: c.id, label: c.countryName }));
    } catch {
      return [];
    }
  }, []);

  const handleSave = async () => {
    const e: Record<string, string> = {};
    if (!form.partyCode.trim()) e.partyCode = 'Code is required';
    if (!form.partyName.trim()) e.partyName = 'Name is required';
    if (taxContext?.jurisdiction) {
      Object.entries(validateTaxFields(taxContext, taxValues)).forEach(([k, v]) => { e[`tax.${k}`] = v; });
    }
    if (Object.keys(e).length) { setErrors(e); return; }
    if (form.partyCode.trim()) {
      try {
        // Copy mode: pass no excludeId (must be unique across all records)
        const excludeId = mode === 'edit' ? record?.id : undefined;
        const ok = await partyService.isCodeAvailable(form.partyCode.trim(), excludeId);
        if (!ok) { setErrors({ partyCode: 'This code already exists.' }); return; }
      } catch { /* server enforces */ }
    }
    setErrors({}); setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      const payload: CreatePartyRequest = {
        partyCode: form.partyCode.trim(), partyName: form.partyName.trim(),
        legalName: form.legalName.trim() || undefined, partyType: form.partyType,
        email: form.email.trim() || undefined, phone: form.phone.trim() || undefined,
        currencyCode: form.currencyCode || undefined,
        creditDays: form.creditDays ? Number(form.creditDays) : undefined,
        partyGroupId: form.partyGroupId || null,
        addressLine1: form.address.trim() || undefined,
        city: form.city.trim() || undefined, postalCode: form.postalCode.trim() || undefined,
        countryId: form.countryId || undefined, stateName: form.stateName.trim() || undefined,
        notes: form.notes.trim() || undefined, statusCode: form.statusCode,
      };
      // Copy mode creates a brand-new record (not an update)
      const saved = (mode === 'edit' && record)
        ? await partyService.update(record.id, { ...payload, rowVersion: record.rowVersion })
        : await partyService.create(payload);
      // The tax profile is saved with the client. A refusal here (a GSTIN in the wrong format, a
      // missing state) is shown, not swallowed: the client row exists, the profile must be fixed.
      if (saved?.id && taxContext?.jurisdiction && (taxContext.fields || []).length > 0) {
        try {
          await taxProfileService.upsertPartyProfile(saved.id, { attributes: taxValues, rowVersion: mode === 'edit' ? taxRowVersionRef.current : 0 });
        } catch (taxErr: any) {
          notificationService.toast.error(`Client saved, but the tax profile was refused: ${taxErr?.response?.data?.message || 'check the Tax panel.'}`);
        }
      }
      setSaving(false);
      const verb = mode === 'edit' ? 'updated' : 'created';
      notificationService.toast.success(`Client "${form.partyName}" ${verb}.`);
      onSaved(); onClose();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the client.');
    }
  };

  if (!isOpen) return null;
  const errCls = (f: string) => `form-input${errors[f] ? ' form-field-error' : ''}`;
  // Fixed header per mode — no (N), no record code (that goes in strip title only)
  const headerText = mode === 'edit' ? 'Edit Client' : mode === 'copy' ? 'Copy Client' : 'Add New Client';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{headerText}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>
        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="partyCode">
              <label className="form-label">Code <span className="text-danger">*</span></label>
              <input className={errCls('partyCode')} value={form.partyCode} maxLength={30}
                readOnly={mode === 'edit'}
                onChange={e => { clearErr('partyCode'); upd({ partyCode: e.target.value.toUpperCase() }); }}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}} />
              {errors.partyCode && <div className="form-error-message">{errors.partyCode}</div>}
            </div>
            <div className="form-group">
              <SyutSelect label="Type" options={PARTY_TYPE_OPTIONS} value={form.partyType}
                onChange={v => upd({ partyType: (v as string) || 'CUSTOMER' })} isClearable={false}
                menuPortalTarget={document.body} />
            </div>
            <div className="form-group" data-field="partyName" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input className={errCls('partyName')} value={form.partyName} maxLength={200}
                onChange={e => { clearErr('partyName'); upd({ partyName: e.target.value }); }} />
              {errors.partyName && <div className="form-error-message">{errors.partyName}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Legal Name</label>
              <input className="form-input" value={form.legalName} maxLength={200}
                onChange={e => upd({ legalName: e.target.value })} />
            </div>
            <div className="form-group">
              <SyutSelect label="Currency"
                isAsync loadOptions={loadCurrencyOptions} defaultOptions cacheOptions={false}
                seedOptions={form.currencyCode
                  ? [{ value: form.currencyCode, label: currencyLabelCache.current.get(form.currencyCode) || form.currencyCode }]
                  : undefined}
                value={form.currencyCode}
                onChange={v => upd({ currencyCode: (v as string) || '' })} isClearable
                placeholder="— use entity default —"
                menuPortalTarget={document.body} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={form.email} maxLength={150}
                onChange={e => upd({ email: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} maxLength={30}
                onChange={e => upd({ phone: e.target.value })} />
            </div>
            <div className="form-group">
              <SyutSelect label="Country" isAsync loadOptions={loadCountries} defaultOptions cacheOptions={false}
                seedOptions={form.countryId
                  ? [{ value: form.countryId, label: countryCache.current.get(form.countryId) || '' }]
                  : undefined}
                menuPortalTarget={document.body} value={form.countryId}
                onChange={v => upd({ countryId: (v as string) || '' })}
                placeholder="Select country" isClearable />
            </div>
            <div className="form-group">
              <label className="form-label">State / Province</label>
              <input className="form-input" value={form.stateName} maxLength={100}
                onChange={e => upd({ stateName: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={form.city} maxLength={100}
                onChange={e => upd({ city: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Postal Code</label>
              <input className="form-input" value={form.postalCode} maxLength={20}
                onChange={e => upd({ postalCode: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Credit Days</label>
              <input type="number" className="form-input" value={form.creditDays} min={0}
                onChange={e => upd({ creditDays: e.target.value })} />
            </div>
            <div className="form-group">
              {/* Customer Group = business house (ADR-0035). Async lookup so newly
                  created groups appear without a reload; blank = Ungrouped. */}
              <SyutSelect
                label="Customer Group"
                isAsync
                cacheOptions={false}
                defaultOptions
                loadOptions={async (input: string) => {
                  const groups = await customerGroupService.getLookup();
                  const term = (input || '').toLowerCase();
                  return groups
                    .filter(g => !term
                      || g.groupName.toLowerCase().includes(term)
                      || g.groupCode.toLowerCase().includes(term))
                    .map(g => ({ value: g.id, label: `${g.groupCode} — ${g.groupName}` }));
                }}
                value={form.partyGroupId}
                onChange={v => upd({ partyGroupId: (v as string) || '' })}
                isClearable
                placeholder="Ungrouped"
                menuPortalTarget={document.body}
              />
            </div>
            <div className="form-group">
              <SyutSelect label="Status" options={STATUS_OPTIONS} value={form.statusCode}
                onChange={v => upd({ statusCode: (v as string) || '' })} isClearable={false}
                menuPortalTarget={document.body} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea className="form-textarea" rows={3} value={form.address}
              placeholder="Street address (multi-line)…"
              onChange={e => upd({ address: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes}
              onChange={e => upd({ notes: e.target.value })} />
          </div>
          <TaxFieldsPanel context={taxContext} values={taxValues}
            errors={Object.fromEntries(Object.entries(errors).filter(([k]) => k.startsWith('tax.')).map(([k, v]) => [k.slice(4), v]))}
            onChange={(key, value) => { setErrors(prev => { if (!prev[`tax.${key}`]) return prev; const n = { ...prev }; delete n[`tax.${key}`]; return n; }); updTax(key, value); }} />
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <IconSystem.check size={16} /> {mode === 'edit' ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`${mode === 'edit' ? 'Save changes to' : 'Create'} client "${form.partyName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'} cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={discardOpen} title="Discard Changes?"
        message="You have unsaved changes. Discard them?" confirmLabel="Discard"
        cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)} />
    </>
  );
};

// ============================================================================
// VIEW MODAL (canonical read-only view — follows the template's
// ServiceMasterViewModal pattern: fixed "Client Details" title, ViewField
// helper, 2-col grid, status-as-badge, minimize support, optional Edit CTA.
// Rules from CLAUDE.md:
//   - Modal-title is FIXED text ("Client Details") — record code / (N) live
//     in the minimized-strip title only.
//   - Uses the canonical ViewField helper (label-above-bordered-box), NOT
//     the legacy Row helper.
//   - Modal ID for minimize is `${tabId}-view-modal-${partyCode}`.
// ============================================================================

const ViewField: React.FC<{ label: string; value?: string | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem',
      background: 'var(--bg-secondary)',
      borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)',
      fontSize: '0.875rem',
      minHeight: '36px',
      whiteSpace: 'pre-wrap',
    }}>
      {value ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

export interface ClientViewModalProps {
  isOpen: boolean;
  party: PartyDto | null;
  onClose: () => void;
  /** Optional: closes View + opens Edit form for the same record. */
  onEdit?: (party: PartyDto) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (partyCode: string) => void;
}

const ClientViewModal: React.FC<ClientViewModalProps> = ({
  isOpen, party, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !party) return;
    minimize({
      id: `${tabId}-view-modal-${party.partyCode}`,
      title: `View - ${party.partyCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(party.partyCode),
    });
    onClose();
  }, [tabId, moduleName, party, minimize, onClose, onRestoreView]);

  if (!isOpen || !party) return null;

  const typeLabel =
    party.partyType === 'CUSTOMER' ? 'Customer'
    : party.partyType === 'SUPPLIER' ? 'Supplier'
    : party.partyType === 'BOTH' ? 'Both' : party.partyType;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">Client Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {/* Their position, before the static details — the question anyone opening a client
              actually has. Both directions, because a party can be customer and supplier at once;
              each side is omitted when there is nothing on it. Scope-bounded (ADR-0042 §6). */}
          <div style={{ marginBottom: '1rem' }}>
            <OutstandingDisplay partyId={party.id} side="both" variant="card" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Code" value={party.partyCode} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px' }}>
                <span className="badge">
                  {party.statusName || party.statusCode || '—'}
                </span>
              </div>
            </div>
            <ViewField label="Name" value={party.partyName} fullWidth />
            <ViewField label="Legal Name" value={party.legalName} fullWidth />
            <ViewField label="Type" value={typeLabel} />
            <ViewField label="Currency" value={party.currencyCode} />
            <ViewField label="Email" value={party.email} />
            <ViewField label="Phone" value={party.phone} />
            <ViewField label="Country" value={party.countryName} />
            <ViewField label="State / Province" value={party.stateName} />
            <ViewField label="City" value={party.city} />
            <ViewField label="Postal Code" value={party.postalCode} />
            <ViewField
              label="Credit Days"
              value={party.creditDays != null ? String(party.creditDays) : null}
            />
            <ViewField label="Address" value={party.addressLine1} fullWidth />
            <ViewField label="Notes" value={party.notes} fullWidth />
            <ViewField label="Created By" value={party.createdByName || party.createdBy} />
            <ViewField
              label="Created At"
              value={party.createdAt ? new Date(party.createdAt).toLocaleString() : null}
            />
            {party.updatedAt && (
              <>
                <ViewField label="Updated By" value={party.updatedByName} />
                <ViewField
                  label="Updated At"
                  value={new Date(party.updatedAt).toLocaleString()}
                />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(party); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================================================
// SCREEN
// ============================================================================

type ViewMode = 'list' | 'grid';

// Structural columns must always be visible; the pattern prevents the
// action menu from being silently hidden when column-visibility state resets.
const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'partyCode', label: 'Code' },
  { id: 'partyName', label: 'Name' },
  { id: 'partyType', label: 'Type' },
  { id: 'email', label: 'Email' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface ClientMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ClientMasterScreen: React.FC<ClientMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('client-master');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('client-master-columns', COLUMN_DEFS);
  const [queryParams, setQueryParams] = useState<PartyQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useParties(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selected, setSelected] = useState<PartyDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // Read-only view modal state — distinct from the edit form state so
  // opening View doesn't clobber a minimized Edit / Add.
  const [viewOpen, setViewOpen] = useState(false);
  const [viewParty, setViewParty] = useState<PartyDto | null>(null);

  // Slot-reuse Add — finds the lowest unused (N) so closed slots get reused.
  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) {
      while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleEdit = useCallback((r: PartyDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.partyCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleView = useCallback((r: PartyDto) => {
    // Restore-on-reopen: if this client's view is already parked, restore it
    // rather than opening a second one (canonical template pattern).
    const viewId = tabId ? `${tabId}-view-modal-${r.partyCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setViewParty(r); setViewOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((partyCode: string) => {
    const rec = data.find(r => r.partyCode === partyCode);
    if (rec) { setViewParty(rec); setViewOpen(true); }
  }, [data]);

  const handleCopy = useCallback((r: PartyDto) => {
    const modalId = tabId ? `${tabId}-form-modal-copy-${r.partyCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    // Auto-minimize any open modal before opening a new copy
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal, formOpen]);

  const handleRestoreModal = useCallback(
    (m: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => {
      if (m === 'create' && instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(r => r.partyCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(m); setFormOpen(true);
    },
    [data],
  );

  const handleDelete = useCallback(async (r: PartyDto) => {
    const ok = await confirmDelete({ resourceLabel: r.partyName });
    if (!ok) return;
    try {
      await partyService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Client "${r.partyName}" deleted.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete.');
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<PartyDto>[] = useMemo(() => [
    { id: 'partyCode', header: 'Code', accessorKey: 'partyCode', size: 120 },
    { id: 'partyName', header: 'Name', accessorKey: 'partyName', size: 240 },
    { id: 'partyType', header: 'Type', accessorKey: 'partyType', size: 110 },
    {
      id: 'email', header: 'Email', accessorKey: 'email', size: 200,
      cell: ({ row }) => (row.original as PartyDto).email || '—',
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => (
        <span className="badge">{(row.original as PartyDto).statusName || 'Active'}</span>
      ),
    },
  ], []);

  // Permission-gated row actions — read is always visible (well, none here — direct Edit),
  // Edit/Copy gated by UPDATE, Delete by DELETE
  const rowActions = useCallback((r: PartyDto): ActionMenuItem[] => {
    // View is always available (no gate) — anyone with grid access can view.
    // Order matches template convention: View → Copy → Edit → Delete.
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Client'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const applyFilters = useCallback(() => {
    setQueryParams(p => ({
      ...p,
      partyType: typeFilter || undefined,
      includeInactive: includeInactive || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [typeFilter, includeInactive]);

  const clearFilters = useCallback(() => {
    setTypeFilter(''); setIncludeInactive(false);
    setQueryParams(p => ({ page: 1, pageSize: p.pageSize }));
  }, []);

  return (
    <EnterpriseScreenShell
      title="Clients"
      description="Customer / supplier master"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.user size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{
        value: searchQuery, onChange: setSearchQuery,
        placeholder: 'Search name / code / email...',
      }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount: [typeFilter, includeInactive ? '1' : ''].filter(Boolean).length,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Type" options={[{ value: '', label: 'All' }, ...PARTY_TYPE_OPTIONS]}
                  value={typeFilter} onChange={v => setTypeFilter((v as string) || '')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={e => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            frozenLeftColumns={['select', 'partyCode']} frozenRightColumns={['actions']}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No clients yet — click Add.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.partyCode}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.partyName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.partyType}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ClientViewModal
        isOpen={viewOpen}
        party={viewParty}
        onClose={() => { setViewOpen(false); setViewParty(null); }}
        onEdit={can('UPDATE') ? (p) => { setViewOpen(false); setViewParty(null); handleEdit(p); } : undefined}
        tabId={tabId}
        moduleName={moduleName || 'Client Master'}
        onRestoreView={handleRestoreView}
      />

      <ClientFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Clients'}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />
    </EnterpriseScreenShell>
  );
};

export default ClientMasterScreen;
